import Input from '../../../Utility/FormElements/Input';
import Select from '../../../Utility/FormElements/Select';
import Checkbox from '../../../Utility/FormElements/Checkbox';
import { StoreEnvironment, StoreI18n, StoreSettings } from '../../../../Types/StoreTypes';
import { EmbeddingModel, LoggerSettings, Model, VoiceModel } from '../../../../Types';
import { Button } from '../../../Utility/Button';
import TextareaAutosize from 'react-textarea-autosize';
import UserSyncButton from '../../Users/UserSyncButton';
import {
    MDXEditor,
    headingsPlugin,
    toolbarPlugin,
    linkPlugin,
    BoldItalicUnderlineToggles,
    BlockTypeSelect,
    ListsToggle,
    CreateLink,
    linkDialogPlugin,
} from '@mdxeditor/editor';

interface GeneralSettingsProps {
    SettingsActions: any;
    i18n: StoreI18n['i18n'];
    windowWidth: StoreEnvironment['windowWidth'];
    getBreakpoint: (breakpoint: string) => number;
    models: Partial<Model>[];
    embeddingModels: Partial<EmbeddingModel>[];
    voiceModels: Partial<VoiceModel>[];
    mdParser: any;
    Currentsettings: StoreSettings;
}

function GeneralSettings({
    models,
    embeddingModels,
    voiceModels,
    mdParser,
    getBreakpoint,
    windowWidth,
    SettingsActions,
    i18n,
    Currentsettings,
}: GeneralSettingsProps) {
    return (
        <div className="w-full flex gap-8 flex-col">
            <h4 className="block">{i18n.translate({ id: 'general', sourceName: 'Settings' })}</h4>
            <label htmlFor="settings_maxCosts_input" className="flex flex-col gap-2">
                {i18n.translate({ id: 'title.costs', sourceName: 'Settings' })}
                <Input
                    inputType="number"
                    value={Currentsettings.maxCosts}
                    onChange={(maxCosts) => SettingsActions.updateCurrentSettings({ maxCosts: maxCosts })}
                    inputProps={{ min: 0, step: 0.1 }}
                    id={'settings_maxCosts_input'}
                />
            </label>
            <label htmlFor="proxyUrlHttps" className="flex flex-col gap-2">
                {i18n.translate({
                    id: 'proxyUrlHttps',
                    sourceName: 'Settings',
                })}
                <Input
                    inputType="string"
                    value={Currentsettings.proxyUrlHttps}
                    onChange={(proxyUrlHttps) =>
                        SettingsActions.updateCurrentSettings({ proxyUrlHttps: proxyUrlHttps })
                    }
                    id={'proxyUrlHttps'}
                />
            </label>
            <label htmlFor="proxyExceptions" className="flex flex-col gap-2">
                {i18n.translate({
                    id: 'proxyExceptions',
                    sourceName: 'Settings',
                })}
                <Input
                    inputType="string"
                    value={Currentsettings.proxyExceptions}
                    onChange={(proxyExceptions) =>
                        SettingsActions.updateCurrentSettings({ proxyExceptions: proxyExceptions })
                    }
                    id={'proxyExceptions'}
                    placeholder={i18n.translate({ id: 'proxyExceptions.placeholder', sourceName: 'Settings' })}
                />
            </label>
            <label htmlFor="proxyCertificatePem" className="flex flex-col gap-2">
                {i18n.translate({
                    id: 'proxyCertificatePem',
                    sourceName: 'Settings',
                })}
                <TextareaAutosize
                    id="proxyCertificatePem"
                    className="bg-input-backgroundColor w-full p-3 border border-2 border-input-borderColor rounded-lg outline-none focus:border-primary transition-all duration-100"
                    value={Currentsettings.proxyCertificatePem}
                    onChange={(e) => SettingsActions.updateCurrentSettings({ proxyCertificatePem: e.target.value })}
                    placeholder={i18n.translate({
                        id: 'proxyCertificatePem.placeholder',
                        sourceName: 'Settings',
                    })}
                />
            </label>
            <label htmlFor="settings_summaryModel_input" className="flex flex-col gap-2">
                {i18n.translate({
                    id: 'summaryModel',
                    sourceName: 'Settings',
                })}

                <Select
                    value={Currentsettings.summaryModel}
                    onChange={(summaryModel) => SettingsActions.updateCurrentSettings({ summaryModel: summaryModel })}
                    id={'settings_summaryModel_input'}
                >
                    {(models ?? [])
                        .filter((model: Partial<Model>) => model !== undefined)
                        .map((model: Partial<Model>) => (
                            <option key={model!.identifier ?? ''} value={model!.identifier ?? ''}>
                                {model!.title ?? ''}
                            </option>
                        ))}
                </Select>
            </label>
            <div className="flex gap-2 items-center">
                <label className="flex items-center space-x-3 cursor-pointer opacity-100">
                    <div className="relative h-6 w-6">
                        <Checkbox
                            checked={Currentsettings.helpLinkVisible}
                            onChange={() =>
                                SettingsActions.updateCurrentSettings({
                                    helpLinkVisible: !Currentsettings.helpLinkVisible,
                                })
                            }
                        />
                    </div>
                    <span>{i18n.translate({ id: 'helpLinkVisible', sourceName: 'Settings' })}</span>
                </label>
            </div>

            <label htmlFor="settings_defaultModel_input" className="flex flex-col gap-2">
                {i18n.translate({
                    id: 'defaultModel',
                    sourceName: 'Settings',
                })}

                <Select
                    value={Currentsettings.defaultModel}
                    onChange={(defaultModel) => SettingsActions.updateCurrentSettings({ defaultModel: defaultModel })}
                    id={'settings_defaultModel_input'}
                >
                    {(models ?? [])
                        .filter((model: Partial<Model>) => model !== undefined)
                        .map((model: Partial<Model>) => (
                            <option key={model!.identifier ?? ''} value={model!.identifier ?? ''}>
                                {model!.title ?? ''}
                            </option>
                        ))}
                </Select>
            </label>

            <label htmlFor="settings_embeddingModel_input" className="flex flex-col gap-2">
                {i18n.translate({
                    id: 'embeddingModel',
                    sourceName: 'Settings',
                })}

                <Select
                    value={Currentsettings.embeddingModel}
                    onChange={(embeddingModel) =>
                        SettingsActions.updateCurrentSettings({ embeddingModel: embeddingModel })
                    }
                    id={'settings_embeddingModel_input'}
                >
                    {(embeddingModels ?? []).map((embeddingModel: Partial<EmbeddingModel>) => (
                        <option key={embeddingModel!.identifier ?? ''} value={embeddingModel!.identifier ?? ''}>
                            {embeddingModel!.title ?? ''}
                        </option>
                    ))}
                </Select>
            </label>

            <label htmlFor="settings_ttsModel_input" className="flex flex-col gap-2">
                {i18n.translate({
                    id: 'ttsModel',
                    sourceName: 'Settings',
                })}

                <Select
                    value={Currentsettings.ttsModel || ''}
                    onChange={(ttsModel) => SettingsActions.updateCurrentSettings({ ttsModel: ttsModel || null })}
                    id={'settings_ttsModel_input'}
                >
                    <option value="">{i18n.translate({ id: 'noModel', sourceName: 'Settings' })}</option>
                    {(voiceModels ?? []).map((voiceModel: Partial<VoiceModel>) => (
                        <option key={voiceModel!.identifier ?? ''} value={voiceModel!.identifier ?? ''}>
                            {voiceModel!.title ?? ''}
                        </option>
                    ))}
                </Select>
            </label>

            <label htmlFor="settings_transcribeModel_input" className="flex flex-col gap-2">
                {i18n.translate({
                    id: 'transcribeModel',
                    sourceName: 'Settings',
                })}

                <Select
                    value={Currentsettings.transcribeModel || ''}
                    onChange={(transcribeModel) =>
                        SettingsActions.updateCurrentSettings({ transcribeModel: transcribeModel || null })
                    }
                    id={'settings_transcribeModel_input'}
                >
                    <option value="">{i18n.translate({ id: 'noModel', sourceName: 'Settings' })}</option>
                    {(voiceModels ?? []).map((voiceModel: Partial<VoiceModel>) => (
                        <option key={voiceModel!.identifier ?? ''} value={voiceModel!.identifier ?? ''}>
                            {voiceModel!.title ?? ''}
                        </option>
                    ))}
                </Select>
            </label>

            <label htmlFor="settings_chatDuration_input" className="flex flex-col gap-2">
                {i18n.translate({
                    id: 'chatDuration',
                    sourceName: 'Settings',
                })}
                <div className="flex gap-2 justify-between">
                    <div className={'min-w-min'}>
                        <Select
                            value={
                                Currentsettings.chatDuration.includes(':')
                                    ? Currentsettings.chatDuration.split(':')[0] + ':'
                                    : Currentsettings.chatDuration
                            }
                            onChange={(chatDuration) =>
                                SettingsActions.updateCurrentSettings({ chatDuration: chatDuration })
                            }
                            id={'settings_chatDurationType_input'}
                        >
                            {[
                                {
                                    text: i18n.translate({ id: 'chatDuration.infinite', sourceName: 'Settings' }),
                                    value: 'infinite',
                                },
                                {
                                    text: i18n.translate({ id: 'chatDuration.after', sourceName: 'Settings' }),
                                    value: 'after:',
                                },
                                {
                                    text: i18n.translate({ id: 'chatDuration.notBefore', sourceName: 'Settings' }),
                                    value: 'not-before:',
                                },
                            ].map((type) => (
                                <option key={type.value} value={type.value}>
                                    {type.text}
                                </option>
                            ))}
                        </Select>
                    </div>
                    <div className={'w-full'}>
                        <Input
                            optionalText={
                                Currentsettings.chatDuration === 'infinite'
                                    ? undefined
                                    : i18n.translate({
                                          id:
                                              'chatDuration.' +
                                              Currentsettings.chatDuration
                                                  .replace('not-before', 'notBefore')
                                                  .split(':')[0] +
                                              '.hint',
                                          sourceName: 'Settings',
                                      })
                            }
                            placeholder={i18n.translate({ id: 'chatDuration.days', sourceName: 'Settings' })}
                            disabled={Currentsettings.chatDuration === 'infinite'}
                            inputType="number"
                            value={Currentsettings.chatDuration.split(':')[1] ?? ''}
                            onChange={(chatDuration) => {
                                SettingsActions.updateCurrentSettings({
                                    chatDuration:
                                        Currentsettings.chatDuration.split(':')[0] +
                                        ':' +
                                        (isNaN(parseInt(chatDuration)) ? '' : chatDuration),
                                });
                            }}
                            inputProps={{ min: 1, step: 1 }}
                            id={'settings_chatDuration_input'}
                        />
                    </div>
                </div>
            </label>

            <label htmlFor="settings_deleteUserAfter_input" className="flex flex-col gap-2">
                {i18n.translate({
                    id: 'userDeletion',
                    sourceName: 'Settings',
                })}
                <Input
                    optionalText={i18n.translate({
                                id:
                                    'userDeletion.' +
                                    (Currentsettings.deleteUserAfter === 0 ? 'immediately' : 'notBefore') +
                                    '.hint',
                                sourceName: 'Settings',
                            })
                    }
                    placeholder={i18n.translate({ id: 'userDeletion.days', sourceName: 'Settings' })}
                    inputType="number"
                    value={Currentsettings.deleteUserAfter}
                    onChange={(deleteUserAfter) => {
                        deleteUserAfter = parseInt(deleteUserAfter)
                        if (!isNaN(deleteUserAfter)) {
                            SettingsActions.updateCurrentSettings({
                                deleteUserAfter: deleteUserAfter
                            });
                        }
                    }}
                    inputProps={{ min: 0, step: 1 }}
                    id={'settings_deleteUserAfter_input'}
                />
            </label>

            {Currentsettings.scimDirection === 'pull' && (
                <label htmlFor="settings_userSyncPullInterval_input" className="flex flex-col gap-2">
                    {i18n.translate({
                        id: 'userSync.pullInterval',
                        sourceName: 'Settings',
                    })}
                    <span className="text-sm text-gray-500">
                        {i18n.translate({
                            id: 'userSync.pullInterval.hint',
                            sourceName: 'Settings',
                            default: 'Step: 5 minutes',
                        })}
                    </span>
                    <div className="flex items-end gap-4">
                        <div className="flex-1">
                            <Input
                                inputType="number"
                                value={Currentsettings.userSyncPullInterval ?? 5}
                                onChange={(userSyncPullInterval) => {
                                    const interval = parseInt(userSyncPullInterval);
                                    if (!isNaN(interval)) {
                                        SettingsActions.updateCurrentSettings({
                                            userSyncPullInterval: interval,
                                        });
                                    }
                                }}
                                inputProps={{ min: 5, step: 5 }}
                                id={'settings_userSyncPullInterval_input'}
                            />
                        </div>
                        <UserSyncButton />
                    </div>
                </label>
            )}

            {(['audit', 'auth', 'dlp', 'connector', 'scim'] as (keyof LoggerSettings)[]).map((logType) => (
                <label htmlFor={`settings_${logType}LogFileDuration_input`} className="flex flex-col gap-2">
                    {i18n
                        .translate({
                            id: logType === 'scim' ? 'maintenance.logFileDuration' : 'compliance.logFileDuration',
                            sourceName: 'Settings',
                        })
                        .replace(
                            '{logType}',
                            { audit: 'Audit', auth: 'Auth', dlp: 'DLP', connector: 'Connector', scim: 'SCIM' }[logType],
                        )}
                    <Input
                        inputType="number"
                        value={Currentsettings.loggerSettings[logType]?.retentionPeriod ?? 30}
                        onChange={(logFileDuration) =>
                            SettingsActions.updateCurrentSettings({
                                loggerSettings: {
                                    ...Currentsettings.loggerSettings,
                                    [logType]: {
                                        ...(Currentsettings.loggerSettings[logType] ?? {}),
                                        retentionPeriod: parseInt(logFileDuration),
                                    },
                                },
                            })
                        }
                        inputProps={{ min: 1, step: 1 }}
                        id={`settings_${logType}LogFileDuration_input`}
                    />
                </label>
            ))}

            <label htmlFor="knowledgebaseSearchMode" className="flex flex-col gap-2">
                {i18n.translate({
                    id: 'knowledgebaseSearchMode',
                    sourceName: 'Settings',
                })}
                <Select
                    value={Currentsettings.knowledgebaseSearchMode || 'hybrid'}
                    onChange={(knowledgebaseSearchMode) =>
                        SettingsActions.updateCurrentSettings({ knowledgebaseSearchMode: knowledgebaseSearchMode })
                    }
                    id="knowledgebaseSearchMode"
                >
                    <option value="hybrid">
                        {i18n.translate({
                            id: 'knowledgebaseSearchMode.hybrid',
                            sourceName: 'Settings',
                        })}
                    </option>
                    <option value="similarity">
                        {i18n.translate({
                            id: 'knowledgebaseSearchMode.similarity',
                            sourceName: 'Settings',
                        })}
                    </option>
                </Select>
            </label>

            <div>
                <label className="flex flex-col gap-2 mb-2">
                    {i18n.translate({
                        id: 'title.protectionText',
                        sourceName: 'Settings',
                    })}
                </label>
                <MDXEditor
                    contentEditableClassName="w-full bg-layout-bodyBackground !text-layout-textcolor p-4 outline-none min-h-[350px]"
                    className="w-full rounded border border-input-borderColor
                    [&_.mdxeditor-toolbar]:bg-input-backgroundColor
                    [&_.mdxeditor-toolbar]:p-4
                    [&_.mdxeditor-toolbar]:text-layout-textcolor
                    [&_.mdxeditor-toolbar_button]:bg-input-backgroundColor
                    [&_.mdxeditor-toolbar_button:hover]:bg-primary
                    [&_.mdxeditor-toolbar_button:hover]:text-layout-textColorOnPrimary
                    [&_.mdxeditor-toolbar_button:hover_svg]:text-layout-textColorOnPrimary
                    [&_.mdxeditor-toolbar_button[data-state='on']]:bg-primary
                    [&_.mdxeditor-toolbar_button[data-state='on']_svg]:text-layout-textColorOnPrimary
                    [&_.mdxeditor-toolbar_button_svg]:text-layout-textcolor
                    [&_.mdxeditor-select-content]:border
                    [&_.mdxeditor-select-content]:border-layout-borderColor
                    [&_.mdxeditor-select-content_div_[role='option']]:bg-layout-bodyBackground
                    [&_.mdxeditor-select-content_div_[role='option']:hover]:bg-input-backgroundHoverColor
                    [&_.mdxeditor-select-content_div]:text-layout-textcolor
                    [&_button[role='combobox']]:bg-input-backgroundColor
                    [&_button[role='combobox']:hover]:bg-input-backgroundHoverColor
                    [&_button[role='combobox']:hover]:text-layout-textcolor
                    [&_button[role='combobox']:hover_svg]:text-layout-textcolor
                    [&_[class*='linkDialogPopoverContent']]:bg-input-backgroundColor
                    [&_[class*='linkDialogPopoverContent']]:border
                    [&_[class*='linkDialogPopoverContent']]:border-layout-borderColor
                    [&_[class*='linkDialogPopoverContent']]:rounded-lg
                    [&_[class*='linkDialogPopoverContent']]:shadow-lg
                    [&_[class*='linkDialogPopoverContent']]:p-3
                    [&_[class*='linkDialogEditForm']_label]:text-layout-textcolor
                    [&_[class*='linkDialogEditForm']_label]:font-medium
                    [&_[class*='linkDialogEditForm']_label]:text-sm
                    [&_[class*='textInput']]:bg-input-backgroundHoverColor
                    [&_[class*='textInput']]:border
                    [&_[class*='textInput']]:border-input-borderColor
                    [&_[class*='textInput']]:rounded-md
                    [&_[class*='textInput']]:p-2
                    [&_[class*='textInput']]:text-layout-textcolor
                    [&_[class*='textInput']:focus]:border-primary
                    [&_[class*='textInput']:focus]:outline-none
                    [&_[class*='downshiftInput']]:bg-input-backgroundColor
                    [&_[class*='downshiftInput']]:border
                    [&_[class*='downshiftInput']]:border-input-borderColor
                    [&_[class*='downshiftInput']]:rounded-md
                    [&_[class*='downshiftInput']]:p-2
                    [&_[class*='downshiftInput']]:text-layout-textcolor
                    [&_[class*='primaryButton']]:leading-[15px]
                    [&_[class*='primaryButton']]:transition
                    [&_[class*='primaryButton']]:duration-200
                    [&_[class*='primaryButton']]:ease-in-out
                    [&_[class*='primaryButton']]:font-normal
                    [&_[class*='primaryButton']]:focus:outline-none
                    [&_[class*='primaryButton']]:py-1
                    [&_[class*='primaryButton']]:mt-2
                    [&_[class*='primaryButton']]:md:py-2
                    [&_[class*='primaryButton']]:px-3
                    [&_[class*='primaryButton']]:md:px-4
                    [&_[class*='primaryButton']]:text-small
                    [&_[class*='primaryButton']]:rounded-button-border-radius
                    [&_[class*='primaryButton']]:bg-button-primary-background
                    [&_[class*='primaryButton']]:text-layout-textColorOnPrimary
                    [&_[class*='primaryButton']]:hover:bg-button-primary-background-hover
                    [&_[class*='primaryButton']:hover]:text-layout-textColorOnPrimary
                    [&_[class*='primaryButton']:hover]:border-button-primary-border
                    [&_[class*='primaryButton']:hover]:border-2
                    [&_[class*='primaryButton']:hover]:border-button-primary-border
                    [&_[class*='secondaryButton']]:leading-[15px]
                    [&_[class*='secondaryButton']]:transition
                    [&_[class*='secondaryButton']]:duration-200
                    [&_[class*='secondaryButton']]:ease-in-out
                    [&_[class*='secondaryButton']]:font-normal
                    [&_[class*='secondaryButton']]:focus:outline-none
                    [&_[class*='secondaryButton']]:mt-2
                    [&_[class*='secondaryButton']]:py-1
                    [&_[class*='secondaryButton']]:md:py-2
                    [&_[class*='secondaryButton']]:px-3
                    [&_[class*='secondaryButton']]:md:px-4
                    [&_[class*='secondaryButton']]:text-small
                    [&_[class*='secondaryButton']]:rounded-button-border-radius
                    [&_[class*='secondaryButton']]:bg-button-secondary-background
                    [&_[class*='secondaryButton']]:text-button-secondary-text
                    [&_[class*='secondaryButton']]:hover:bg-button-secondary-background-hover
                    [&_[class*='secondaryButton']]:hover:text-button-secondary-text-hover
                    [&_[class*='secondaryButton']]:hover:border-button-secondary-border
                    [&_[class*='secondaryButton']]:hover:border-2
                    [&_[class*='secondaryButton']]:hover:border-button-secondary-border
                    [&_[class*='linkDialogPreviewAnchor']]:text-primary
                    [&_[class*='linkDialogPreviewAnchor']]:no-underline
                    [&_[class*='linkDialogPreviewAnchor']:hover]:underline
                    [&_[class*='actionButton']]:p-2
                    [&_[class*='actionButton']]:rounded
                    [&_[class*='actionButton']]:text-layout-textcolor
                    [&_[class*='actionButton']:hover]:bg-input-backgroundHoverColor
                    [&_[class*='popoverArrow']]:fill-input-backgroundHoverColor
                    "
                    markdown={Currentsettings.dataProtectionText}
                    onChange={(markdown) =>
                        SettingsActions.updateCurrentSettings({
                            dataProtectionText: markdown,
                        })
                    }
                    translation={(key, defaultValue, interpolations) => {
                        const translations: Record<string, string> = {
                            'toolbar.bold': i18n.translate({ id: 'mdxEditor.bold', sourceName: 'Settings' }),
                            'toolbar.removeBold': i18n.translate({
                                id: 'mdxEditor.removeBold',
                                sourceName: 'Settings',
                            }),
                            'toolbar.italic': i18n.translate({ id: 'mdxEditor.italic', sourceName: 'Settings' }),
                            'toolbar.removeItalic': i18n.translate({
                                id: 'mdxEditor.removeItalic',
                                sourceName: 'Settings',
                            }),
                            'toolbar.underline': i18n.translate({ id: 'mdxEditor.underline', sourceName: 'Settings' }),
                            'toolbar.removeUnderline': i18n.translate({
                                id: 'mdxEditor.removeUnderline',
                                sourceName: 'Settings',
                            }),
                            'toolbar.bulletedList': i18n.translate({
                                id: 'mdxEditor.bulletedList',
                                sourceName: 'Settings',
                            }),
                            'toolbar.numberedList': i18n.translate({
                                id: 'mdxEditor.numberedList',
                                sourceName: 'Settings',
                            }),
                            'toolbar.blockTypeSelect.selectBlockTypeTooltip': i18n.translate({
                                id: 'mdxEditor.selectBlockType',
                                sourceName: 'Settings',
                            }),
                            'toolbar.blockTypeSelect.placeholder': i18n.translate({
                                id: 'mdxEditor.blockType',
                                sourceName: 'Settings',
                            }),
                            'toolbar.blockTypes.paragraph': i18n.translate({
                                id: 'mdxEditor.paragraph',
                                sourceName: 'Settings',
                            }),
                            'toolbar.blockTypes.quote': i18n.translate({
                                id: 'mdxEditor.quote',
                                sourceName: 'Settings',
                            }),
                            'toolbar.blockTypes.heading': i18n.translate({
                                id: 'mdxEditor.heading',
                                sourceName: 'Settings',
                            }),
                            'toolbar.link': i18n.translate({
                                id: 'mdxEditor.createLink',
                                sourceName: 'Settings',
                            }),
                            'createLink.url': i18n.translate({
                                id: 'mdxEditor.createLink.url',
                                sourceName: 'Settings',
                            }),
                            'createLink.urlPlaceholder': i18n.translate({
                                id: 'mdxEditor.createLink.urlPlaceholder',
                                sourceName: 'Settings',
                            }),
                            'createLink.text': i18n.translate({
                                id: 'mdxEditor.createLink.text',
                                sourceName: 'Settings',
                            }),
                            'createLink.textTooltip': i18n.translate({
                                id: 'mdxEditor.createLink.textTooltip',
                                sourceName: 'Settings',
                            }),
                            'createLink.title': i18n.translate({
                                id: 'mdxEditor.createLink.title',
                                sourceName: 'Settings',
                            }),
                            'createLink.titleTooltip': i18n.translate({
                                id: 'mdxEditor.createLink.titleTooltip',
                                sourceName: 'Settings',
                            }),
                            'createLink.saveTooltip': i18n.translate({
                                id: 'mdxEditor.createLink.saveTooltip',
                                sourceName: 'Settings',
                            }),
                            'createLink.cancelTooltip': i18n.translate({
                                id: 'mdxEditor.createLink.cancelTooltip',
                                sourceName: 'Settings',
                            }),
                            'linkPreview.open': i18n.translate({
                                id: 'mdxEditor.linkPreview.open',
                                sourceName: 'Settings',
                            }),
                            'linkPreview.edit': i18n.translate({
                                id: 'mdxEditor.linkPreview.edit',
                                sourceName: 'Settings',
                            }),
                            'linkPreview.copyToClipboard': i18n.translate({
                                id: 'mdxEditor.linkPreview.copyToClipboard',
                                sourceName: 'Settings',
                            }),
                            'linkPreview.copied': i18n.translate({
                                id: 'mdxEditor.linkPreview.copied',
                                sourceName: 'Settings',
                            }),
                            'linkPreview.remove': i18n.translate({
                                id: 'mdxEditor.linkPreview.remove',
                                sourceName: 'Settings',
                            }),
                            'dialogControls.save': i18n.translate({
                                id: 'mdxEditor.dialogControls.save',
                                sourceName: 'Settings',
                            }),
                            'dialogControls.cancel': i18n.translate({
                                id: 'mdxEditor.dialogControls.cancel',
                                sourceName: 'Settings',
                            }),
                        };
                        let result = translations[key] || defaultValue;
                        if (interpolations) {
                            Object.entries(interpolations).forEach(([k, v]) => {
                                result = result.replace(`{{${k}}}`, String(v));
                            });
                        }
                        return result;
                    }}
                    plugins={[
                        headingsPlugin(),
                        linkPlugin(),
                        linkDialogPlugin(),
                        toolbarPlugin({
                            toolbarContents: () => (
                                <>
                                    <BoldItalicUnderlineToggles />
                                    <BlockTypeSelect />
                                    <ListsToggle options={['bullet', 'number']} />
                                    <CreateLink />
                                </>
                            ),
                        }),
                    ]}
                />
            </div>

            <div className="mt-5 text-right">
                <Button onClick={SettingsActions.handleUpdateSettings}>
                    {i18n.translate({ id: 'save', sourceName: 'Main' })}
                </Button>
            </div>
        </div>
    );
}

export default GeneralSettings;
